import * as React from 'react'

import { Combobox } from '#/shared/ui/combobox'
import type { ComboboxOption, ComboboxAction } from '#/shared/ui/combobox'
import type { CustomerListItem } from '#/shared/api/generated/shipment/models'

import { useComboboxFreeze } from '../model/use-combobox-freeze'
import { useCustomerSearch } from '../model/use-customer-search'

interface BusinessComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onCustomerSelect?: (customer: CustomerListItem) => void
  onClear?: () => void
  label?: string
  placeholder?: string
  error?: boolean
  className?: string
  suffix?: React.ReactNode
  action?: ComboboxAction
  searchDisabled?: boolean
}

const ALWAYS_PASS_FILTER = () => true

const EMPTY_LABEL = '—'

function customerLabel(customer: CustomerListItem): string {
  if (customer.company) return customer.company
  const name = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(' ')
  return name || EMPTY_LABEL
}

function customerTag(customer: CustomerListItem): string | undefined {
  if (!customer.company) return undefined
  const name = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(' ')
  return name || undefined
}

function toOption(customer: CustomerListItem): ComboboxOption {
  return {
    value: customer.id,
    label: customerLabel(customer),
    tag: customerTag(customer),
  }
}

export function BusinessCombobox({
  value,
  onValueChange,
  onCustomerSelect,
  onClear,
  label,
  placeholder,
  error,
  className,
  suffix,
  action,
  searchDisabled = false,
}: BusinessComboboxProps) {
  const freeze = useComboboxFreeze<CustomerListItem>(value)
  const live = useCustomerSearch(value, {
    enabled: freeze.isUserTyped && !searchDisabled && !freeze.isJustSelected,
  })
  const items = freeze.pickItems(live.items)
  const options: ComboboxOption[] = searchDisabled ? [] : items.map(toOption)

  const handleValueChange = (next: string) => {
    freeze.onTyped(next)
    onValueChange(next)
  }

  const handleSelect = (option: ComboboxOption) => {
    freeze.freeze(option.label, items)
    const customer = items.find((it) => it.id === option.value)
    if (customer) onCustomerSelect?.(customer)
  }

  return (
    <Combobox
      label={label}
      placeholder={placeholder}
      options={options}
      value={value}
      onValueChange={handleValueChange}
      onSelect={handleSelect}
      onClear={onClear}
      filter={ALWAYS_PASS_FILTER}
      loading={!searchDisabled && live.isLoading}
      hideEmpty={searchDisabled || live.needsMoreInput || !freeze.isUserTyped}
      hideToggle
      error={error}
      className={className}
      suffix={suffix}
      action={searchDisabled ? undefined : action}
      clearable
    />
  )
}
